import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  Database,
  Calendar,
  BarChart2,
  LineChart,
  ChevronDown,
  Search,
} from 'lucide-react';
import { collection, onSnapshot } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { MarbleLoader } from '../ui/LoadingStates';

const AVAILABLE_COLLECTIONS = [
  { value: 'applications', label: 'Applications', color: '#2563EB' },
  { value: 'bif_applications', label: 'BIF Applications', color: '#10B981' },
  { value: 'clients', label: 'Clients', color: '#8B5CF6' },
  { value: 'coaches', label: 'Coaches', color: '#F59E0B' },
  { value: 'moderator', label: 'Business Advisors', color: '#EF4444' },
  { value: 'messages', label: 'Messages', color: '#06B6D4' },
  { value: 'interviews', label: 'Interviews', color: '#EC4899' },
  { value: 'tasks', label: 'Tasks', color: '#14B8A6' },
  { value: 'notifications', label: 'Notifications', color: '#6366F1' },
  { value: 'users', label: 'Users', color: '#84CC16' },
  { value: 'alumni', label: 'Alumni', color: '#F97316' },
  { value: 'programs', label: 'Programs', color: '#3B82F6' },
  { value: 'submissions', label: 'Submissions', color: '#A855F7' },
  { value: 'documentBank', label: 'Documents', color: '#10B981' },
  { value: 'weeklyReports', label: 'Weekly Reports', color: '#F59E0B' },
];

const DATE_FIELDS = ['createdAt', 'updatedAt', 'timestamp', 'date', 'created', 'updated'];

const extractDate = doc => {
  for (const key of DATE_FIELDS) {
    const v = doc[key];
    if (!v) continue;
    if (v?.toDate) return v.toDate();
    if (v?.seconds) return new Date(v.seconds * 1000);
    if (typeof v === 'string' || v instanceof Date) {
      const parsed = new Date(v);
      if (!isNaN(parsed.getTime())) return parsed;
    }
  }
  return null;
};

export default function EngagementTrends() {
  const [selectedCollections, setSelectedCollections] = useState(['applications']);
  const [chartType, setChartType] = useState('bar');
  const [chartData, setChartData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState('');

  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setDate(new Date().getDate() - 6)),
    to: new Date(),
  });

  useEffect(() => {
    const unsubs = [];
    const allData = [];

    if (selectedCollections.length === 0) {
      setChartData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    selectedCollections.forEach(col => {
      const ref = collection(firestore, col);
      const unsub = onSnapshot(
        ref,
        snap => {
          snap.docs.forEach(doc => {
            const d = doc.data();
            const date = extractDate(d);
            if (!date) return;
            allData.push({ _id: doc.id, _source: col, date });
          });

          const filtered = allData.filter(i => i.date >= dateRange.from && i.date <= dateRange.to);

          const grouped = filtered.reduce((acc, i) => {
            const key = i.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            acc[key] = acc[key] || { key, date: i.date };
            selectedCollections.forEach(c => {
              acc[key][c] = acc[key][c] || 0;
            });
            acc[key][i._source]++;
            return acc;
          }, {});

          const formatted = Object.values(grouped).sort(
            (a, b) => new Date(a.date) - new Date(b.date)
          );
          setChartData(formatted);
          setLoading(false);
        },
        err => {
          setError(`Error loading ${col}`);
          setLoading(false);
        }
      );
      unsubs.push(unsub);
    });

    return () => unsubs.forEach(u => u());
  }, [selectedCollections, dateRange]);

  const configs = selectedCollections
    .map(s => AVAILABLE_COLLECTIONS.find(c => c.value === s))
    .filter(Boolean);

  const maxVal = useMemo(() => {
    let max = 1;
    chartData.forEach(p =>
      configs.forEach(cfg => {
        if ((p[cfg.value] || 0) > max) max = p[cfg.value];
      })
    );
    return max;
  }, [chartData, configs]);

  const hasData = chartData.some(p => configs.some(cfg => (p[cfg.value] || 0) > 0));

  const toggleCollection = name => {
    setSelectedCollections(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const filteredCollections = AVAILABLE_COLLECTIONS.filter(c =>
    c.label.toLowerCase().includes(search.toLowerCase())
  );

  const linePath = key => {
    const pts = chartData.map((p, i) => {
      const val = p[key] || 0;
      const x = (i / (chartData.length - 1 || 1)) * 100;
      const y = 100 - (val / maxVal) * 100;
      return [x, y];
    });
    return pts.reduce(
      (acc, [x, y], i) =>
        i === 0 ? `M ${x},${y}` : `${acc} C ${x - 1},${y} ${x + 1},${y} ${x},${y}`,
      ''
    );
  };

  return (
    <motion.div
      className="w-full rounded-2xl border border-gray-200 bg-white/90 p-6 shadow-xl backdrop-blur-sm dark:border-gray-700 dark:bg-gray-900/80"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      {/* Header */}
      <div className="mb-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <h3 className="flex items-center gap-2 text-2xl font-bold text-gray-800 dark:text-gray-100">
          <TrendingUp className="h-7 w-7 text-emerald-500" />
          Engagement Trends
        </h3>
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Database className="h-4 w-4" />
          Live Sync Enabled
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        {/* Collection Dropdown */}
        <div className="dropdown-container relative">
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            <Database className="h-4 w-4" />
            {selectedCollections.length
              ? `${selectedCollections.length} Selected`
              : 'Select Collections'}
            <ChevronDown
              className={`h-4 w-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {dropdownOpen && (
            <div className="absolute left-0 top-full z-50 mt-2 w-72 rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2 dark:border-gray-700">
                <Search className="h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search collections..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-gray-700 placeholder-gray-400 focus:outline-none dark:text-gray-200"
                />
              </div>
              <div className="max-h-64 overflow-y-auto p-2">
                {filteredCollections.map(c => (
                  <label
                    key={c.value}
                    className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCollections.includes(c.value)}
                      onChange={() => toggleCollection(c.value)}
                      className="h-4 w-4 accent-blue-600"
                    />
                    <div className="h-3 w-3 rounded-full" style={{ backgroundColor: c.color }} />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Chart Type Toggle */}
        <div className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white p-1 dark:border-gray-600 dark:bg-gray-800">
          {[
            { type: 'bar', icon: BarChart2, label: 'Bar' },
            { type: 'line', icon: LineChart, label: 'Line' },
          ].map(({ type, icon: Icon, label }) => (
            <button
              key={type}
              onClick={() => setChartType(type)}
              className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                chartType === type
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Date Range */}
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          {['from', 'to'].map(f => (
            <input
              key={f}
              type="date"
              value={dateRange[f].toISOString().slice(0, 10)}
              onChange={e => setDateRange({ ...dateRange, [f]: new Date(e.target.value) })}
              className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Chart */}
      <div className="relative flex h-80 items-end justify-center rounded-2xl border border-gray-100 bg-gradient-to-br from-gray-50 via-white to-emerald-50 dark:border-gray-800 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        {loading ? (
          <div className="flex h-full w-full items-center justify-center">
            <MarbleLoader size="md" />
          </div>
        ) : hasData ? (
          <>
            {chartType === 'bar' ? (
              <div className="flex h-64 w-full items-end gap-1 px-4 pb-4">
                {chartData.map((period, idx) => (
                  <div key={idx} className="flex flex-1 flex-col items-center">
                    <div className="flex w-full items-end justify-center gap-1">
                      {configs.map(cfg => {
                        const v = period[cfg.value] || 0;
                        const h = (v / maxVal) * 100;
                        return (
                          <motion.div
                            key={cfg.value}
                            className="rounded-t-md"
                            style={{
                              height: `${h}%`,
                              background: cfg.color,
                              width: `${100 / configs.length}%`,
                            }}
                            whileHover={{ scaleY: 1.05 }}
                            title={`${cfg.label}: ${v}`}
                          />
                        );
                      })}
                    </div>
                    <span className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      {period.key}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                className="absolute inset-0"
              >
                {configs.map(cfg => (
                  <motion.path
                    key={cfg.value}
                    d={linePath(cfg.value)}
                    fill="none"
                    stroke={cfg.color}
                    strokeWidth="0.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1 }}
                  />
                ))}
              </svg>
            )}
          </>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center text-center">
            <BarChart2 className="mb-3 h-12 w-12 text-gray-400 dark:text-gray-600" />
            <p className="text-base font-medium text-gray-600 dark:text-gray-300">
              No engagement data available
            </p>
            <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
              Select collections or adjust date range to visualize activity.
            </p>
          </div>
        )}
      </div>

      {/* Legend */}
      {hasData && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
          {configs.map(cfg => (
            <div key={cfg.value} className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full" style={{ backgroundColor: cfg.color }} />
              <span className="text-sm text-gray-600 dark:text-gray-400">{cfg.label}</span>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}
